import { style, globalStyle } from '@vanilla-extract/css'
import { vars, focusRing } from '../design/theme.css'

// Where the masthead's five word-labels stop fitting beside the wordmark. MEASURED, not
// guessed: the labels, the EN|GA pair and the account mark need ~480px between them, and the
// mark plus the wordmark another ~220 — so below about 780 the row wants more than the page
// has. It used to take it anyway, and the wordmark broke across two lines mid-name ("Culfin /
// Birds") on a 768 tablet while the whole page scrolled sideways on a 375 phone.
//
// Below COMPACT the nav shows its icons and the wordmark stands down to the mark — the phone
// treatment this design already had, now starting where the words actually stop fitting rather
// than at a round 560. TABLET is the last step above it, where the wordmark and the gaps come
// down a notch to hold one line. `flex-wrap` on the row is the backstop under all of it: a
// station with a longer name than this one wraps rather than overflowing.
const COMPACT = 'screen and (max-width: 780px)'
const PHONE = COMPACT
const TABLET = 'screen and (min-width: 781px) and (max-width: 980px)'

// The content row shared by masthead, main and footer — centred, capped at the
// (wider) page max, with responsive gutters.
const row = {
  width: '100%',
  maxWidth: vars.layout.pageMax,
  marginInline: 'auto',
  paddingInline: vars.layout.gutter,
} as const

// ---- Masthead: a single sticky, condensing header (the one sticky we keep) ----
export const topbar = style({
  position: 'sticky',
  top: 0,
  zIndex: vars.z.sticky,
  background: vars.color.paper,
  borderBottom: `1px solid ${vars.color.ink}`,
})
export const topbarScrolled = style({ boxShadow: vars.shadow.edgeLg })

export const top = style({
  ...row,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: vars.space[5],
  paddingBlock: `${vars.space[5]} ${vars.space[4]}`,
  transition: `padding ${vars.dur.med} ease`,
  '@media': { [TABLET]: { gap: vars.space[4] } },
})
globalStyle(`${topbarScrolled} ${top}`, { paddingBlock: vars.space[3] })

export const brand = style({
  display: 'flex',
  alignItems: 'center',
  flex: 'none',
  gap: vars.space[3],
  padding: 0,
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  color: 'inherit',
  textDecoration: 'none',
})
export const mark = style({
  height: '46px',
  width: 'auto',
  display: 'block',
  transition: `height ${vars.dur.med} ease`,
})
globalStyle(`:root[data-theme="dark"] ${mark}`, { filter: 'invert(1)' })
globalStyle(`${topbarScrolled} ${mark}`, { height: '32px' })

export const word = style({
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs['2xl'],
  lineHeight: 1,
  letterSpacing: vars.ls.word,
  whiteSpace: 'nowrap',
  transition: `font-size ${vars.dur.med} ease`,
  '@media': { [TABLET]: { fontSize: vars.fs.xl } },
})
globalStyle(`${brand}:focus-visible`, focusRing)
globalStyle(`${topbarScrolled} ${word}`, { fontSize: vars.fs.xl })

export const nav = style({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  gap: vars.space[5],
  '@media': { [TABLET]: { gap: vars.space[3] } },
})
export const navItem = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  letterSpacing: vars.ls.caps,
  lineHeight: vars.lh.snug,
  textTransform: 'uppercase',
  color: vars.color.inkSoft,
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  textDecoration: 'none',
  transition: `color ${vars.dur.fast} ease`,
  selectors: {
    '&:hover': { color: vars.color.ink },
    '&:focus-visible': focusRing,
    // The current tab, marked the same way on every width. On the phone this used to be
    // overridden to a bare GREEN glyph — the state carried by colour alone (§18), in the green
    // that means "Green list" everywhere else on the site. The underline sits under the glyph
    // there instead, so the mark survives being read without colour.
    '&[aria-current="page"]': {
      color: vars.color.ink,
      textDecoration: 'underline',
      textUnderlineOffset: vars.uline.mark,
    },
  },
})
export const navIcon = style({ display: 'none' })
export const navLabel = style({})

export const lang = style({ display: 'inline-flex', alignItems: 'center' })
// The language pair is a NAV control and sits in the nav row, so it takes the nav's voice —
// ms.md + ls.caps. It was ms.sm + ls.label, and SIGN IN beside it was ms.sm + ls.caps: three
// sizes and two trackings across one 40px-tall row, for one role.
export const langOpt = style({
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  padding: `${vars.space[1]} ${vars.space[2]}`,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  letterSpacing: vars.ls.caps,
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
  transition: `color ${vars.dur.fast} ease`,
  selectors: {
    '&:hover': { color: vars.color.ink },
    '&:focus-visible': focusRing,
    // Marked the same way every other live option in the app is marked (§5).
    '&[data-on="true"]': {
      color: vars.color.ink,
      textDecoration: 'underline',
      textUnderlineOffset: vars.uline.mark,
    },
    '& + &': { borderLeft: `1px solid ${vars.color.rule}` },
  },
})

// ---- Main editorial envelope ----
export const main = style({ ...row, paddingBlock: vars.space[6] })

// ---- Footer: quiet, with the Sources link and a second way to About ----
export const foot = style({
  ...row,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: vars.space[3],
  paddingBlock: vars.space[6],
  marginTop: vars.space[7],
  borderTop: `1px solid ${vars.color.rule}`,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.caps,
  color: vars.color.inkSoft,
})
export const footDot = style({ color: vars.color.inkSoft })
export const footCoords = style({ color: vars.color.inkSoft })
export const footLinks = style({
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: `${vars.space[2]} ${vars.space[4]}`,
  marginLeft: 'auto',
})
export const footLink = style({
  background: 'transparent',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  letterSpacing: 'inherit',
  textTransform: 'uppercase',
  color: vars.color.inkSoft,
  whiteSpace: 'nowrap',
  transition: `color ${vars.dur.fast} ease`,
  selectors: { '&:hover': { color: vars.color.ink }, '&:focus-visible': focusRing },
})

// ---- Responsive: phone shows nav icons, hides wordmark + nav labels ----
globalStyle(`${word}`, { '@media': { [PHONE]: { display: 'none' } } })
globalStyle(`${mark}`, { '@media': { [PHONE]: { height: '34px' } } })
// Tighter column gap on the phone so the FIVE tab icons + EN|GA + the account control all clear
// 375px. Measured, not estimated: at space[3] the row ran 33px PAST a 375px viewport and every
// page on the site scrolled sideways because of it. space[2] plus the trimmed lang padding below
// brings it inside, and `top`/`nav` now wrap rather than overflow if a sixth item is ever added.
globalStyle(`${nav}`, { '@media': { [PHONE]: { gap: `${vars.space[1]} ${vars.space[2]}` } } })
globalStyle(`${lang}`, { '@media': { [PHONE]: { marginInline: `-${vars.space[1]}` } } })
// Real targets where a finger has to find them. The ink stays exactly as small as it looks —
// only the box grows — so the masthead reads the same and can actually be tapped: the nav
// glyphs and the EN|GA pair measured 30x21 and 23x22 on a phone (§5, §18).
globalStyle(`${langOpt}`, {
  '@media': {
    [COMPACT]: {
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: vars.ctrl.lg,
      // The box grows DOWN, not sideways: widening it pushed the 375px row over again.
      padding: `0 ${vars.space[1]}`,
    },
  },
})
globalStyle(`${navItem}`, {
  '@media': {
    [COMPACT]: { display: 'inline-flex', alignItems: 'center', minHeight: vars.ctrl.lg },
  },
})
globalStyle(`${navLabel}`, { '@media': { [PHONE]: { display: 'none' } } })
globalStyle(`${navIcon}`, {
  '@media': { [PHONE]: { display: 'inline-flex', fontSize: vars.mark.iconLg, letterSpacing: 0 } },
})
globalStyle(`${top}`, { '@media': { [PHONE]: { gap: `${vars.space[2]} ${vars.space[3]}` } } })
globalStyle(`${foot}`, { '@media': { [PHONE]: { gap: vars.space[2] } } })
globalStyle(`${footLinks}`, { '@media': { [PHONE]: { marginLeft: 0, width: '100%' } } })
